import { debounce } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useCartState } from 'supplier-portals/uxbeauty/contexts/cart/CartContext';
import { CartState } from 'supplier-portals/uxbeauty/contexts/cart/types';

/**
 * Synchronizes the form state with the cart context
 *
 * It's separate so that we don't need to use `watch` to observe the form state on top level,
 * which would cause a re-render on every keystroke.
 *
 * Cannot be a hook because it needs to be inside a FormProvider to perform nicely.
 *
 */
export const CartStateSynchronizer: React.FC = () => {
  const { setValue } = useCartState();

  // Debounce the context update to avoid unnecessary re-renders which come with every update.
  // Before extending this logic, please consider using a library, for example Jotai:
  // https://jotai.org/docs/recipes/atom-with-debounce
  const setValueDebounced = useMemo(() => debounce(setValue, 1000), [setValue]);
  const formValues = useWatch<CartState>();
  useEffect(() => {
    // The cast is not very nice here, but necessary because RHF applies a deep partial type
    // so formValues don't match anymore with the CartState type. This case is only relevant
    // in case defaultValues are not provided to useForm.
    setValueDebounced(prev => ({ ...prev, ...(formValues as CartState) }));
    return () => {
      setValueDebounced.clear();
    };
  }, [formValues, setValue, setValueDebounced]);

  return null;
};
